import { useMemo, useRef } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { UrgencyHourDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { useChartTokens } from '../../ServicesStatistics/chartTokens'
import { escapeHtml, formatHour } from '../../ServicesStatistics/statisticsUtils'
import { UrgencyMeasure } from '../urgencyAnalysis'
import { formatCount, formatMinutes } from '../urgencySummaryFormat'

interface UrgencyHourlyChartProps {
  /** Las horas de llegada (hasta 24); las que falten se dibujan en cero. */
  hours: UrgencyHourDTO[]
  measure: UrgencyMeasure
  ariaLabel: string
  height?: number
}

const DEFAULT_HEIGHT = 320

/** «13:00 a 13:59». */
const slotText = (intl: IntlShape, hour: number): string =>
  intl.formatMessage({ id: 'UrgencySummaryHourSlot' }, { from: formatHour(hour), to: `${String(hour).padStart(2, '0')}:59` })

function tooltipHtml(intl: IntlShape, point: UrgencyHourDTO, measure: UrgencyMeasure): string {
  const text = (id: string, values?: Record<string, string | number>) => escapeHtml(intl.formatMessage({ id }, values))
  const lines = [`<div class="nv-urg-tip__title">${escapeHtml(slotText(intl, point.hour))}</div>`]
  if (measure === 'arrivals') {
    lines.push(`<div>${text('UrgencySummarySeriesArrivals')}: ${formatCount(intl, point.arrivals)}</div>`)
    lines.push(`<div>${text('UrgencySummarySeriesLeft')}: ${formatCount(intl, point.leftWithoutAttention)}</div>`)
  } else if (point.avgWaitMinutes === null) {
    lines.push(`<div>${text('UrgencySummaryTipNoWait')}</div>`)
  } else {
    lines.push(`<div>${text('UrgencySummarySeriesWait')}: ${escapeHtml(formatMinutes(intl, point.avgWaitMinutes))}</div>`)
    lines.push(`<div>${text('UrgencySummaryTipWaited', { count: point.waitedCount, countText: formatCount(intl, point.waitedCount) })}</div>`)
  }
  return `<div class="nv-urg-tip">${lines.join('')}</div>`
}

/**
 * Llegadas (barras) o espera media (línea) por hora de llegada, de las 0 a las 23 h, con una sola serie. Las horas sin
 * pacientes atendidos con espera medida quedan en blanco en la línea (no cuentan como cero minutos). El dibujo es solo
 * visual: el lector de pantalla lee la descripción (`ariaLabel`) y las frases que acompañan al gráfico.
 * TODO(design-system): gráficos. Novarium no define el estilo de los gráficos ni los colores de series.
 */
export default function UrgencyHourlyChart({ hours, measure, ariaLabel, height = DEFAULT_HEIGHT }: UrgencyHourlyChartProps) {
  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const tokens = useChartTokens(ref)
  const isBar = measure === 'arrivals'

  const points = useMemo<UrgencyHourDTO[]>(
    () => Array.from({ length: 24 }, (_, hour) => hours.find((item) => item.hour === hour)
      ?? { hour, arrivals: 0, attended: 0, leftWithoutAttention: 0, waitedCount: 0, avgWaitMinutes: null, waitOver60: 0 }),
    [hours],
  )

  const series = useMemo(() => [{
    name: intl.formatMessage({ id: isBar ? 'UrgencySummarySeriesArrivals' : 'UrgencySummarySeriesWait' }),
    data: points.map((point) => (isBar ? point.arrivals : point.avgWaitMinutes)),
  }], [intl, points, isBar])

  const options = useMemo<ApexOptions | null>(() => {
    if (!tokens) return null
    return {
      chart: {
        type: isBar ? 'bar' : 'line',
        height,
        fontFamily: tokens.font,
        foreColor: tokens.text2,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      colors: [tokens.primary],
      plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
      stroke: { width: isBar ? 0 : 2, curve: 'straight', lineCap: 'round' },
      markers: { size: isBar ? 0 : 3, strokeWidth: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: { borderColor: tokens.divider, strokeDashArray: 0 },
      xaxis: {
        categories: points.map((point) => String(point.hour).padStart(2, '0')),
        axisBorder: { color: tokens.divider },
        axisTicks: { color: tokens.divider },
        tooltip: { enabled: false },
        labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '12px' } },
        title: {
          text: intl.formatMessage({ id: 'UrgencySummaryAxisHour' }),
          style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        title: {
          text: intl.formatMessage({ id: isBar ? 'UrgencySummaryAxisPatients' : 'UrgencySummaryAxisMinutes' }),
          style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
        },
        labels: {
          style: { fontSize: '12px' },
          formatter: (value: number) => (isBar ? formatCount(intl, Math.round(value)) : `${formatCount(intl, Math.round(value))} min`),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => tooltipHtml(intl, points[dataPointIndex], measure),
      },
    }
  }, [intl, tokens, points, isBar, measure, height])

  return (
    <div ref={ref} className="nv-urg-chart" role="img" aria-label={ariaLabel}>
      {options && <ReactApexChart key={measure} type={isBar ? 'bar' : 'line'} height={height} series={series} options={options} />}
    </div>
  )
}
